import React from 'react';
import { useSelector } from 'react-redux';
import { getDashboardsState } from '@/store-selectors';
import styled, { css } from 'styled-components';
import { useDashboardId } from '@/hooks';
import { modalInteraction } from '@/actions';
import { useAppDispatch } from '@/store/hooks';
import DashboardFilters from '@/components/DashboardFilters_LEGACY';
import {
  useApplyFiltersMutation,
  useGetDashboardFiltersQuery,
} from '@/features/customer-facing-dashboards/apiSlice';
import { DASHBOARDS_VIEW } from '@/util/enums/EnumModals';
import { Button, Small, TrevorIcon } from '@trevor/ui-components';
import LayerWhere from '@/components/layers/LayerWhere';

const EmbeddedDashboardFilterBarContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  position: sticky;
  position: -webkit-sticky;
  padding: 14px 8px 7px 8px;

  z-index: 1;
`;

const reusableContainerStyles = css`
  background: var(--color-interface-neutral-light);
  box-shadow: var(--elevation-soft);
  border-radius: var(--radius-24);
`;

const FilterBarContainer = styled.div`
  ${reusableContainerStyles};
  padding: var(--spacing-s) var(--spacing-s) var(--spacing-s) 20px;

  > div {
    padding: unset;
  }
`;

const RightContainer = styled.div`
  display: flex;
  gap: var(--spacing-s);
`;

const PoweredByContainer = styled.div`
  ${reusableContainerStyles};
  cursor: pointer;
  margin-left: auto;
  padding: var(--spacing-s) var(--spacing-l);
  display: flex;
  align-items: center;
  gap: calc(var(--spacing-xxs) * 2);

  > p {
    color: var(--color-interface-content-lighter);
    text-decoration: none;
  }

  > svg {
    filter: grayscale(100%);
    opacity: 60%;
    transition-duration: var(--transition-normal);
  }

  &:hover {
    > svg {
      filter: unset;
      opacity: unset;
    }
  }
`;

export const EmbeddedDashboardFilterBar: React.FC = () => {
  const dashboardState = useSelector(getDashboardsState);

  const dashboardId = useDashboardId();
  const dispatch = useAppDispatch();
  const dashboardsState = useSelector(getDashboardsState);

  useGetDashboardFiltersQuery(
    {
      dashboardId,
      refreshId: dashboardState.refreshId,
    },
    { skip: !dashboardState.refreshId },
  );
  const [applyFilters] = useApplyFiltersMutation();

  const showFilters = Boolean(
    dashboardState.filters.length > 0 &&
      dashboardState.chartWidgets &&
      dashboardState.chartWidgets.length > 0,
  );
  const handleApplyFilters = (filters: LayerWhere[]) => {
    applyFilters({ dashboardId, appliedFilters: filters });
  };

  const handleUpdateFilters = (filters: LayerWhere[]) => {
    dispatch(
      modalInteraction(DASHBOARDS_VIEW, {
        section: 'applied-filters',
        filters,
      }),
    );
    handleApplyFilters(filters);
  };

  return (
    <EmbeddedDashboardFilterBarContainer>
      {showFilters && (
        <FilterBarContainer>
          <DashboardFilters
            onUpdated={handleUpdateFilters}
            readonly
            onReloadFilters={() => null}
          />
        </FilterBarContainer>
      )}
      <RightContainer>
        <Button
          variant="secondary"
          icon={<TrevorIcon name="refresh" />}
          onClick={() => handleApplyFilters(dashboardsState.appliedFilters)}
        />
        <PoweredByContainer
          onClick={() => window.open('https://trevor.io/', '_blank')}
        >
          <Small>Powered by</Small>
          <TrevorIcon name="logo-wordmark" height="10" width="60" />
        </PoweredByContainer>
      </RightContainer>
    </EmbeddedDashboardFilterBarContainer>
  );
};
